//src/hooks/useSurveyResults.ts
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  approveSurveyCampaignAiRisk,
  getSurveyCampaignResponseSummary,
  generateSurveyCampaignAiSuggestions,
  getSurveyCampaignResults,
  listSurveyCampaignAiSuggestions,
  listSurveyCampaignCuts,
  listSurveyRiskSuggestions,
  rejectSurveyCampaignAiRisk,
  rejectSurveyCampaignAiSuggestion,
  resetSurveyCampaignAiRisk,
  rebuildSurveyCampaignAnalytics,
  undoSurveyCampaignAiRiskApproval,
  updateSurveyCampaignAiSuggestionFeedback,
} from "@/services/surveyResults";
import type { SurveyRebuildAnalyticsIn } from "@/types/surveyResults";

export function useSurveyCampaignResponseSummary(campaignId: string | undefined) {
  return useQuery({
    queryKey: ["survey_campaign_response_summary", campaignId],
    queryFn: () => getSurveyCampaignResponseSummary(campaignId as string),
    enabled: !!campaignId,
    staleTime: 15_000,
  });
}

export function useSurveyCampaignResults(campaignId: string | undefined) {
  return useQuery({
    queryKey: ["survey_campaign_results", campaignId],
    queryFn: () => getSurveyCampaignResults(campaignId as string),
    enabled: !!campaignId,
    staleTime: 15_000,
  });
}

export function useSurveyCampaignCuts(campaignId: string | undefined, params: { cut_type?: string }) {
  return useQuery({
    queryKey: ["survey_campaign_cuts", campaignId, params],
    queryFn: () => listSurveyCampaignCuts(campaignId as string, params),
    enabled: !!campaignId,
    staleTime: 15_000,
  });
}

export function useSurveyRiskSuggestions(
  campaignId: string | undefined,
  params: { cut_type?: string; risk_level_code?: string; page?: number; per_page?: number },
) {
  return useQuery({
    queryKey: ["survey_risk_suggestions_campaign", campaignId, params],
    queryFn: () => listSurveyRiskSuggestions(campaignId as string, params),
    enabled: !!campaignId,
    staleTime: 15_000,
  });
}


export function useSurveyCampaignAiSuggestions(
  campaignId: string | undefined,
  params: { page?: number; per_page?: number },
) {
  return useQuery({
    queryKey: ["survey_campaign_ai_suggestions", campaignId, params],
    queryFn: () => listSurveyCampaignAiSuggestions(campaignId as string, params),
    enabled: !!campaignId,
    staleTime: 15_000,
  });
}

export function useRebuildSurveyCampaignAnalytics() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, payload }: { campaignId: string; payload: SurveyRebuildAnalyticsIn }) =>
      rebuildSurveyCampaignAnalytics(campaignId, payload),
    onSuccess: (_data, variables) => {
      const campaignId = variables.campaignId;
      qc.invalidateQueries({ queryKey: ["survey_campaign_results", campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_cuts", campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_risk_suggestions_campaign", campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_response_summary", campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_detail", campaignId] });
    },
  });
}

export function useGenerateSurveyCampaignAiSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId }: { campaignId: string }) => generateSurveyCampaignAiSuggestions(campaignId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["ai_wallet"] });
      qc.invalidateQueries({ queryKey: ["ai_wallet_transactions"] });
      qc.invalidateQueries({ queryKey: ["ai_usage_logs"] });
      qc.invalidateQueries({ queryKey: ["ai_history"] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_ai_suggestions", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_campaign_results", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["survey_risk_suggestions_campaign", variables.campaignId] });
    },
  });
}


export function useRejectSurveyCampaignAiSuggestion() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, outputId }: { campaignId: string; outputId: string }) =>
      rejectSurveyCampaignAiSuggestion(campaignId, outputId),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["survey_campaign_ai_suggestions", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["ai_history"] });
    },
  });
}


function invalidateCampaignAiReview(qc: ReturnType<typeof useQueryClient>, campaignId: string) {
  qc.invalidateQueries({ queryKey: ["survey_campaign_ai_suggestions", campaignId] });
  qc.invalidateQueries({ queryKey: ["survey_campaign_results", campaignId] });
  qc.invalidateQueries({ queryKey: ["survey_risk_suggestions_campaign", campaignId] });
  qc.invalidateQueries({ queryKey: ["risk_inventory_items"] });
  qc.invalidateQueries({ queryKey: ["ai_history"] });
}

export function useApproveSurveyCampaignAiRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, outputId, riskIndex }: { campaignId: string; outputId: string; riskIndex: number }) =>
      approveSurveyCampaignAiRisk(campaignId, outputId, riskIndex),
    onSuccess: (_data, variables) => invalidateCampaignAiReview(qc, variables.campaignId),
  });
}

export function useRejectSurveyCampaignAiRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, outputId, riskIndex }: { campaignId: string; outputId: string; riskIndex: number }) =>
      rejectSurveyCampaignAiRisk(campaignId, outputId, riskIndex),
    onSuccess: (_data, variables) => invalidateCampaignAiReview(qc, variables.campaignId),
  });
}

export function useResetSurveyCampaignAiRisk() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, outputId, riskIndex }: { campaignId: string; outputId: string; riskIndex: number }) =>
      resetSurveyCampaignAiRisk(campaignId, outputId, riskIndex),
    onSuccess: (_data, variables) => invalidateCampaignAiReview(qc, variables.campaignId),
  });
}

export function useUndoSurveyCampaignAiRiskApproval() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, outputId, riskIndex }: { campaignId: string; outputId: string; riskIndex: number }) =>
      undoSurveyCampaignAiRiskApproval(campaignId, outputId, riskIndex),
    onSuccess: (_data, variables) => invalidateCampaignAiReview(qc, variables.campaignId),
  });
}


export function useUpdateSurveyCampaignAiSuggestionFeedback() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ campaignId, outputId, feedbackStatus }: { campaignId: string; outputId: string; feedbackStatus: "like" | "unlike" | null }) =>
      updateSurveyCampaignAiSuggestionFeedback(campaignId, outputId, feedbackStatus),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: ["survey_campaign_ai_suggestions", variables.campaignId] });
      qc.invalidateQueries({ queryKey: ["ai_history"] });
    },
  });
}
